// WHAT: Component for displaying workspace breakdown as pie chart with pagination
// WHY:  Provides visual breakdown of time spent across workspaces with sorting and pagination
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: recharts for charting, useFilter hook, core.invoke
// RISKS:
//   - NOTE: Workspace aggregation from category_chart may be incorrect
//   - NOTE: Color assignment may not match workspace colors from config
//   - NOTE: Pagination may cause confusion with large datasets

import type { JSX } from "react";

import { ChevronUpDownIcon, FunnelIcon } from "@heroicons/react/24/outline";
import { core } from "@tauri-apps/api";
import { useState, useEffect, useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from "recharts";

import { Card } from "@/components/ui";

import { useFilter } from "./FilterContextProvider.shared";

/**
 * WHAT:
 *   Returns color for workspace based on name.
 *
 * @param workspace - Workspace name
 *
 * @returns Hex color code for the workspace
 *
 */
const getWorkspaceColor = (workspace: string): string => {
  const colors: Record<string, string> = {
    Investments: "#FFFFFF", // Weiß
    Nexus: "#3B82F6", // Blau
    "Real Estate": "#8B5CF6", // Lila
    Reselling: "#F59E0B", // Orange
    TracQ: "#EAB308", // Gelb
    Trading: "#10B981", // Grün
  };
  return colors[workspace] ?? "#6B7280"; // Fallback zu Grau
};

/**
 * Workspace data interface
 */
interface WorkspaceData {
  /** Display color */
  color: string;
  /** Duration in minutes */
  duration: number;
  /** Workspace name */
  name: string;
}

/**
 * Aspect entry interface (from backend)
 */
interface AspectEntry {
  /** Label (workspace/aspect format) */
  label: string;
  /** Minutes */
  minutes: number;
}

/**
 * Backend response interface
 */
interface BackendResponse {
  /** Category chart data */
  category_chart?: AspectEntry[];
}

/**
 * WHAT:
 *   Component that displays workspace breakdown as pie chart with sorting,
 *   pagination, and filtering capabilities.
 *
 * WHY:
 *   Enables users to visualize time distribution across workspaces with
 *   interactive controls for large datasets.
 *
 * BEHAVIOR:
 *   - Fetches aggregated data from backend
 *   - Aggregates aspect entries by workspace
 *   - Displays pie chart with workspace breakdown
 *   - Supports sorting by name or duration
 *   - Supports pagination for large datasets
 *   - Updates when filter changes
 *
 * RISKS:
 *   - NOTE: Workspace aggregation from category_chart may be incorrect
 *   - NOTE: Color assignment may not match workspace colors from config
 *   - NOTE: Pagination may cause confusion with large datasets
 *
 * @returns JSX.Element
 *
 */
export const WorkspacesBreakdown = () => {
  const { dateFilter } = useFilter();

  const [data, setData] = useState<WorkspaceData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);

  const [sortKey, setSortKey] = useState<"duration" | "name">("duration");
  const [asc, setAsc] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(25);

  // NOTE: Load data from backend
  useEffect(() => {
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await core.invoke<BackendResponse>("get_aggregated_summary", {
          filter: dateFilter,
        });

        // NOTE: Convert category_chart to WorkspaceData by aggregating by workspace
        if (res.category_chart) {
          const workspaceMap = new Map<string, { minutes: number; color: string }>();

          for (const entry of res.category_chart) {
            // Extrahiere Workspace aus dem Label (z.B. "Nexus [Programming]" -> "Nexus")
            const workspace = entry.label.split(" [")[0] ?? "";
            const existing = workspaceMap.get(workspace) ?? {
              color: getWorkspaceColor(workspace),
              minutes: 0,
            };
            existing.minutes += entry.minutes;
            workspaceMap.set(workspace, existing);
          }

          const workspaceData: WorkspaceData[] = [...workspaceMap.entries()].map(
            ([name, data]) => ({
              color: data.color,
              duration: data.minutes,
              name,
            }),
          );

          setData(workspaceData);
        } else {
          setData([]);
        }
      } catch {
        setError("Fehler beim Laden der Daten");
      } finally {
        setLoading(false);
      }
    })();
  }, [dateFilter]);

  // Sortierung
  const sortedData = useMemo(() => {
    const sorted = [...data];
    sorted.sort((a, b) => {
      let aValue: number | string;
      let bValue: number | string;

      if (sortKey === "name") {
        aValue = a.name;
        bValue = b.name;
      } else {
        aValue = a.duration;
        bValue = b.duration;
      }

      if (aValue < bValue) {
        return asc ? -1 : 1;
      }
      if (aValue > bValue) {
        return asc ? 1 : -1;
      }
      return 0;
    });
    return sorted;
  }, [data, sortKey, asc]);

  // Pagination
  const totalPages = Math.ceil(sortedData.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  const toggleSort = (key: "duration" | "name") => {
    setAsc(key === sortKey ? !asc : true);
    setSortKey(key);
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    return `${String(hours)}h ${mins.toString().padStart(2, "0")}m`;
  };

  /**
   * WHAT:
   *   Returns table content based on loading, error, or data state.
   *
   * @returns JSX.Element for table content
   *
   */
  const getTableContent = (): JSX.Element => {
    if (loading) {
      return (
        <div className="h-40 flex items-center justify-center text-slate-500 animate-pulse">
          Loading…
        </div>
      );
    }

    if (error) {
      return <div className="h-40 flex items-center justify-center text-red-400">{error}</div>;
    }

    if (data.length === 0) {
      return (
        <div className="h-40 flex items-center justify-center text-slate-300">
          Keine Workspace-Daten verfügbar
        </div>
      );
    }

    return (
      <>
        {/* Tabelle Header */}
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-600">
                <th
                  className="px-4 py-3 cursor-pointer select-none hover:text-white transition-colors text-left text-slate-400"
                  onClick={() => {
                    toggleSort("name");
                  }}
                >
                  <div className="flex items-center gap-2">
                    Workspaces
                    <ChevronUpDownIcon className="w-4 h-4" />
                  </div>
                </th>
                <th
                  className="px-4 py-3 cursor-pointer select-none hover:text-white transition-colors text-left text-slate-400"
                  onClick={() => {
                    toggleSort("duration");
                  }}
                >
                  <div className="flex items-center gap-2">
                    Duration
                    <ChevronUpDownIcon className="w-4 h-4" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((workspace) => (
                <tr
                  className="border-b border-slate-700 hover:bg-slate-700/50 transition-colors"
                  key={workspace.name}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: workspace.color }}
                      />
                      <span className="text-white font-medium">{workspace.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-white font-semibold">
                    {formatDuration(workspace.duration)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between text-sm text-slate-400">
          <div>
            Showing {startIndex + 1} to {Math.min(endIndex, sortedData.length)} of{" "}
            {sortedData.length} entries
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-2 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600/50"
              disabled={currentPage === 1}
              onClick={() => {
                setCurrentPage(1);
              }}
            >
              &lt;&lt;
            </button>
            <button
              className="px-2 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600/50"
              disabled={currentPage === 1}
              onClick={() => {
                setCurrentPage(Math.max(1, currentPage - 1));
              }}
            >
              &lt;
            </button>
            <span className="px-2 py-1 bg-tracq-bright-blue text-white rounded">{currentPage}</span>
            <button
              className="px-2 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600/50"
              disabled={currentPage === totalPages}
              onClick={() => {
                setCurrentPage(Math.min(totalPages, currentPage + 1));
              }}
            >
              &gt;
            </button>
            <button
              className="px-2 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600/50"
              disabled={currentPage === totalPages}
              onClick={() => {
                setCurrentPage(totalPages);
              }}
            >
              &gt;&gt;
            </button>
            <select
              className="bg-slate-700/50 border border-slate-500/50 text-slate-200 text-xs px-2 py-1 rounded ml-2"
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
              }}
              value={entriesPerPage}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </>
    );
  };

  /**
   * WHAT:
   *   Returns chart content based on loading, error, or data state.
   *
   * @returns JSX.Element for chart content
   *
   */
  const getChartContent = (): JSX.Element => {
    if (loading) {
      return (
        <div className="h-64 flex items-center justify-center text-slate-500 animate-pulse">
          Loading…
        </div>
      );
    }

    if (error) {
      return <div className="h-64 flex items-center justify-center text-red-400">{error}</div>;
    }

    if (data.length === 0) {
      return (
        <div className="h-64 flex items-center justify-center text-slate-300">
          Keine Daten für Donut Chart
        </div>
      );
    }

    return (
      <>
        {/* Donut Chart */}
        <div className="h-64">
          <ResponsiveContainer height="100%" width="100%">
            <PieChart>
              <Pie
                cx="50%"
                cy="50%"
                data={data}
                dataKey="duration"
                innerRadius={60}
                nameKey="name"
                outerRadius={100}
                paddingAngle={2}
              >
                {data.map((entry) => (
                  <Cell fill={entry.color} key={`cell-${entry.name}`} />
                ))}
              </Pie>
              <Legend
                formatter={(value: string) => <span style={{ color: "#94A3B8" }}>{value}</span>}
                height={36}
                verticalAlign="bottom"
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Linke Seite - Tabelle */}
      <Card
        className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
        padding="lg"
        variant="elevated"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-semibold text-white">Workspaces</h4>
            <div className="flex items-center gap-2">
              <FunnelIcon className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-400">Filter</span>
            </div>
          </div>

          {getTableContent()}
        </div>
      </Card>

      {/* Rechte Seite - Donut Chart */}
      <Card
        className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
        padding="lg"
        variant="elevated"
      >
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-white">Distribution</h4>

          {getChartContent()}
        </div>
      </Card>
    </div>
  );
};
